import React from 'react'

import Link from 'next/link'

import type { TypedObject } from '@portabletext/types'

import classNames from 'classnames'

import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import type { ImageProps } from '~/components/atoms/Image/Image'
import Image from '~/components/atoms/Image/Image'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { resolveLink } from '~/components/serializers'
import { ComponentTheme } from '~/components/themes'

import ArticleAuthor from '../ArticleAuthor/ArticleAuthor'

import styles from './TeamHighlightCard.module.css'

type HighlightVariant = 'variant1' | 'variant2' | 'variant3'

interface BaseHighlightProps {
  variant: HighlightVariant
  link: {
    text: string
    externalLink: string
    internalLink: any
    target: string
  }
  text: TypedObject[]
}

interface ImageVariantProps extends BaseHighlightProps {
  variant: 'variant1' | 'variant3'
  image: ImageProps
  title: TypedObject[]
  author?: never
  subTitle?: never
  theme?: never
}

interface AuthorVariantProps extends BaseHighlightProps {
  variant: 'variant2'
  image?: never
  subTitle?: string
  title: TypedObject[]
  author: {
    slug: string
    name: string
    image: ImageProps
  }
  theme?: ComponentTheme
}

export type TeamHighlightCardProps = ImageVariantProps | AuthorVariantProps

interface WrapperProps {
  children: React.ReactNode
  href: string
  theme?: ComponentTheme
}

const Wrapper: React.FC<WrapperProps> = ({
  children,
  href,
  theme = ComponentTheme.secondary,
}) => {
  return (
    <Link
      href={href || '#'}
      className={classNames(styles.wrapper, {
        [styles.dark]: theme === ComponentTheme.black,
        [styles.light]: theme === ComponentTheme.secondary,
      })}
    >
      {children}
    </Link>
  )
}

const TeamHighlightCard: React.FC<TeamHighlightCardProps> = ({
  variant,
  image,
  subTitle,
  title,
  text,
  link,
  author,
  theme = ComponentTheme.secondary,
}) => {
  const renderTitle = () => {
    return (
      <div className={styles.textBlock}>
        <BlockContent value={title}></BlockContent>
      </div>
    )
  }

  const renderLink = () => (
    <Text size={TextSizes.primaryTitleSml} className={styles.link}>
      {link.text}
    </Text>
  )

  if (variant === 'variant1') {
    return (
      <Wrapper href={resolveLink(link)} theme={theme}>
        {image && (
          <div className={styles.variant1ImageContainer}>
            <Image
              asset={image.asset}
              alt={image.alt || 'Team highlight'}
              objectFit="contain"
            />
          </div>
        )}
        <div className={styles.variant1ContentContainer}>
          <div>
            <div className={styles.variant1Intro}>{renderTitle()}</div>
            <div className={styles.variant1Text}>
              <div className={styles.titleBlock}>
                <BlockContent value={text}></BlockContent>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.floatingLink}>{renderLink()}</div>
      </Wrapper>
    )
  }

  if (variant === 'variant2') {
    return (
      <Wrapper href={resolveLink(link)} theme={theme}>
        <div
          className={classNames(styles.variant2Container, {
            [styles.dark]: theme === ComponentTheme.black,
            [styles.light]: theme === ComponentTheme.secondary,
          })}
        >
          <Text className={styles.variant2SubTitle}>{subTitle}</Text>
          <div className={styles.variant2QuoteContainer}>
            <div className={styles.variant2Text}>
              <div className={styles.textBlock}>
                <BlockContent value={text}></BlockContent>
              </div>
            </div>
            {author && (
              <div className={styles.variant2Author}>
                <ArticleAuthor
                  author={{ ...author, slug: `/${author.slug}` }}
                  preText="featuring"
                  showDefault={false}
                  centered={false}
                />
              </div>
            )}
          </div>
          <div className={styles.floatingLink}>{renderLink()}</div>
        </div>
      </Wrapper>
    )
  }

  if (variant === 'variant3') {
    return (
      <Wrapper href={resolveLink(link)} theme={theme}>
        {image && (
          <div className={styles.variant3BkgImage}>
            <Image
              asset={image.asset}
              alt={image.alt || 'Team highlight'}
              objectFit="cover"
            />
          </div>
        )}
        <div
          className={classNames(styles.variant2TextContainer, {
            [styles.dark]: theme === ComponentTheme.black,
            [styles.light]: theme === ComponentTheme.secondary,
          })}
        >
          {renderTitle()}
          <div
            className={classNames(styles.titleBlock, styles.variant3TextBlock)}
          >
            <BlockContent value={text}></BlockContent>
          </div>
          {renderLink()}
        </div>
      </Wrapper>
    )
  }

  return null
}

export default TeamHighlightCard
